import {
    EXCLUDE_SOLD_OUT_FILTER,
    itemColumnsAtom,
    itemFilterAtom,
} from '@activities/items/ItemsActivity/atoms/itemFilterAtoms';
import Button from '@components/Button';
import Checkbox from '@components/Checkbox';
import Column from '@components/Column';
import Radio from '@components/Radio';
import Row from '@components/Row';
import Text from '@components/Text';
import styled from '@emotion/styled';
import useGoBack from '@hooks/useGoBack';
import { BottomSheet } from '@stackflow/plugin-basic-ui';
import { ActivityComponentType } from '@stackflow/react';
import { useAtom } from 'jotai';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

declare module '@stackflow/config' {
    interface Register {
        ItemFilterBottomSheet: {};
    }
}

const COLUMN_OPTIONS = [2, 3] as const;

const ItemFilterBottomSheet: ActivityComponentType<'ItemFilterBottomSheet'> = () => {
    const { t } = useTranslation('items');
    const goBack = useGoBack();
    const [filters, setFilters] = useAtom(itemFilterAtom);
    const [columns, setColumns] = useAtom(itemColumnsAtom);
    const [excludeSoldOut, setExcludeSoldOut] = useState(
        filters.includes(EXCLUDE_SOLD_OUT_FILTER),
    );
    const [selectedColumns, setSelectedColumns] = useState(columns);

    const handleApply = () => {
        const otherFilters = filters.filter((f) => f !== EXCLUDE_SOLD_OUT_FILTER);
        setFilters(excludeSoldOut ? [...otherFilters, EXCLUDE_SOLD_OUT_FILTER] : otherFilters);
        setColumns(selectedColumns);
        goBack();
    };

    return (
        <BottomSheet>
            <Header>
                <Text size='title' fontWeight='bold' align='center'>
                    {t('filter.title')}
                </Text>
            </Header>
            <Contents fullWidth>
                <Section spacing={2} fullWidth>
                    <Text fontWeight='bold'>{t('filter.filterLabel')}</Text>
                    <Option>
                        <Checkbox isChecked={excludeSoldOut}
                            toggleCheckbox={() => setExcludeSoldOut((prev) => !prev)}>
                            {t('filter.excludeSoldOut')}
                        </Checkbox>
                    </Option>
                </Section>
                <Section spacing={2} fullWidth>
                    <Text fontWeight='bold'>{t('filter.sortLabel')}</Text>
                    <Column fullWidth>
                        {COLUMN_OPTIONS.map((option) => (
                            <Option key={option}>
                                <Radio value={`${option}`}
                                    isSelected={selectedColumns === option}
                                    onChange={() => setSelectedColumns(option)}>
                                    {t(option === 2 ? 'filter.twoColumns' : 'filter.threeColumns')}
                                </Radio>
                            </Option>
                        ))}
                    </Column>
                </Section>
            </Contents>
            <Footer spacing={2} fullWidth>
                <ActionButton variant='tone' onClick={goBack}>
                    {t('filter.close')}
                </ActionButton>
                <ActionButton onClick={handleApply}>
                    {t('filter.apply')}
                </ActionButton>
            </Footer>
        </BottomSheet>
    );
};

const Header = styled.div`
    align-items: center;
    display: flex;
    height: 64px;
    justify-content: center;
`;

const Contents = styled(Column)`
    padding: 0 var(--spacing-4);
`;

const Section = styled(Column)`
    padding: var(--spacing-4) 0;

    &:not(:last-of-type) {
        border-bottom: 1px solid var(--color-border-default);
    }
`;

const Option = styled.div`
    padding: var(--spacing-2) 0;
    width: 100%;
`;

const Footer = styled(Row)`
    padding: var(--spacing-4) var(--spacing-4) calc(var(--spacing-4) + var(--safe-area-bottom));
`;

const ActionButton = styled(Button)`
    flex: 1;
`;

export default ItemFilterBottomSheet;
